import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';

// ---------------------------------------------------------------------------
// A mill's wharf.
//
// The mills stand inland of the Zaanse Schans coast, so their goods came down
// to the water — a short deck on posts with the mill's own cargo stacked on
// it, which is what tells you from a distance which mill you are looking at.
// ---------------------------------------------------------------------------

export function registerWharfBlocks(palette) {
    return {
        plank: palette.add('wharfPlank', 0x9c7f52, { jitter: 0.08 }),
        post: palette.add('wharfPost', 0x5b452f, { jitter: 0.07 }),
        barrel: palette.add('wharfBarrel', 0x7a4a2c, { jitter: 0.06 }),
        hoop: palette.add('wharfHoop', 0x4a4a50, { jitter: 0.05 }),
        // What each mill sends down to the water.
        timber: palette.add('goodTimber', 0xc8a56a, { jitter: 0.07 }),
        mustard: palette.add('goodMustard', 0xd9b23a, { jitter: 0.05 }),
        pigment: palette.add('goodPigment', 0x9c4a6e, { jitter: 0.06 }),
    };
}

const LEN = 13;
const W = 7;

/** Local +X runs out to sea; the landward end is at x = 0. */
export function createWharf(palette, material, P, { voxelSize, goods }) {
    const g = new VoxelGrid(LEN, 14, W);
    const cz = (W - 1) >> 1;

    // Deck on posts. The posts run from the deck down past the waterline —
    // the deck stands clear of the swell, so they have further to go.
    g.box(0, 3, cz - 1, LEN - 1, 3, cz + 1, P.plank);
    for (let x = 1; x < LEN; x += 4) {
        for (const z of [cz - 1, cz + 1]) g.box(x, -3, z, x, 2, z, P.post);
    }

    // Bollards at the seaward end.
    g.box(LEN - 2, 4, cz - 1, LEN - 2, 5, cz - 1, P.post);
    g.box(LEN - 2, 4, cz + 1, LEN - 2, 5, cz + 1, P.post);

    // The cargo, stacked at the landward end — a different shape per mill, so
    // you can tell them apart from the water without reading anything.
    const good = P[goods] || P.barrel;
    if (goods === 'timber') {
        // Sawn planks, stacked flat.
        for (let y = 4; y <= 6; y++) g.box(1, y, cz - 1, 4, y, cz + 1, good);
    } else if (goods === 'mustard') {
        // Barrels, hooped.
        for (const [x, z] of [[1, cz - 1], [2, cz + 1], [3, cz]]) {
            g.box(x, 4, z, x, 6, z, good);
            g.set(x, 5, z, P.hoop);
        }
    } else {
        // Casks of pigment, smaller and stacked square.
        g.box(1, 4, cz - 1, 3, 5, cz + 1, good);
        g.box(2, 6, cz, 2, 6, cz, good);
    }

    const mesh = new THREE.Mesh(
        g.build(palette, {
            voxelSize,
            origin: new THREE.Vector3(0, -3 * voxelSize, -cz * voxelSize),
        }),
        material,
    );
    mesh.name = `wharf:${goods}`;
    return { mesh, length: LEN * voxelSize, halfWidth: (W / 2) * voxelSize };
}
